import {useEffect, useMemo, useState} from 'react';

import {
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import {C, STATUS_CONFIG} from '../constants';
import {
    normalizeProductDetailFields,
    resetProductDetailFieldSettings,
} from '../../shared/productDetails';

export function SectionLabel({children, sx = {}}) {
    return (
        <Typography
            sx={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--cg-text-soft)',
                textTransform: 'uppercase',
                letterSpacing: '.07em',
                ...sx,
            }}>
            {children}
        </Typography>
    );
}

export function StatusPill({status, onClick}) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;

    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: `1px solid ${config.border}`,
                background: config.bg,
                color: config.text,
                borderRadius: 999,
                padding: '3px 10px',
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
            }}>
            <Box
                component="span"
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: config.dot,
                    display: 'block',
                    flexShrink: 0,
                }}
            />
            {config.label}
        </ButtonBase>
    );
}

const formatVarLabel = (name = '') =>
    String(name)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const makeFieldFromVarName = (name = '', index = 0) => ({
    id: `field-${name || 'custom'}-${Date.now()}-${index}`,
    label: formatVarLabel(name || 'Field'),
    names: name ? [name] : [],
    enabled: true,
});

export function ProductDetailFieldsDialog({
    availableVars = [],
    error = '',
    fields = [],
    onClose,
    onSave,
    open,
    saving = false,
}) {
    const [draftFields, setDraftFields] = useState(() =>
        normalizeProductDetailFields({fields}),
    );

    useEffect(() => {
        if (open) setDraftFields(normalizeProductDetailFields({fields}));
    }, [fields, open]);

    const variableOptions = useMemo(() => {
        const options = new Map();

        draftFields.forEach((field) => {
            field.names.forEach((name) => {
                if (name) options.set(name, {name, sample: ''});
            });
        });

        availableVars.forEach((variable) => {
            const name = typeof variable?.name === 'string' ? variable.name.trim() : '';
            if (!name) return;
            const rawSample = variable.sample ?? variable.value ?? '';
            options.set(name, {
                name,
                sample: typeof rawSample === 'string' ? rawSample : String(rawSample || ''),
            });
        });

        return Array.from(options.values()).sort((left, right) =>
            left.name.localeCompare(right.name),
        );
    }, [availableVars, draftFields]);

    const updateField = (id, patch) => {
        setDraftFields((current) =>
            current.map((field) =>
                field.id === id ? {...field, ...patch} : field,
            ),
        );
    };

    const moveField = (id, direction) => {
        setDraftFields((current) => {
            const index = current.findIndex((field) => field.id === id);
            const nextIndex = index + direction;
            if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;

            const next = [...current];
            const [field] = next.splice(index, 1);
            next.splice(nextIndex, 0, field);
            return next;
        });
    };

    const addField = () => {
        const selectedNames = new Set(draftFields.map((field) => field.names[0]));
        const nextVar = variableOptions.find((variable) => !selectedNames.has(variable.name));
        setDraftFields((current) => [
            ...current,
            makeFieldFromVarName(nextVar?.name || '', current.length),
        ]);
    };

    const save = async () => {
        await onSave({fields: draftFields});
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: 800}}>
                Product fields
            </DialogTitle>
            <DialogContent dividers sx={{background: C.surfaceAlt}}>
                <Stack gap="10px">
                    {draftFields.map((field, index) => (
                        <Box
                            key={field.id}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '32px 1fr',
                                    md: '32px minmax(150px,.8fr) minmax(180px,1fr) auto',
                                },
                                gap: '8px',
                                alignItems: 'center',
                                background: C.surface,
                                border: `1px solid ${C.border}`,
                                borderRadius: '8px',
                                padding: '8px',
                            }}>
                            <Checkbox
                                checked={field.enabled}
                                onChange={(event) =>
                                    updateField(field.id, {enabled: event.target.checked})
                                }
                                size="small"
                                sx={{padding: 0}}
                            />
                            <TextField
                                label="Label"
                                value={field.label}
                                onChange={(event) =>
                                    updateField(field.id, {label: event.target.value})
                                }
                                size="small"
                                sx={{minWidth: 0}}
                            />
                            <Select
                                value={field.names[0] || ''}
                                onChange={(event) => {
                                    const name = event.target.value;
                                    updateField(field.id, {
                                        names: name ? [name] : [],
                                        label: field.label || formatVarLabel(name),
                                    });
                                }}
                                displayEmpty
                                size="small"
                                sx={{minWidth: 0}}>
                                <MenuItem value="">
                                    <em>Select variable</em>
                                </MenuItem>
                                {variableOptions.map((variable) => (
                                    <MenuItem key={variable.name} value={variable.name}>
                                        {variable.name}
                                        {variable.sample ? ` - ${variable.sample}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Stack
                                direction="row"
                                gap="6px"
                                sx={{gridColumn: {xs: '1 / -1', md: 'auto'}}}>
                                <ButtonBase
                                    disabled={index === 0}
                                    onClick={() => moveField(field.id, -1)}
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        borderRadius: '7px',
                                        padding: '7px 9px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        opacity: index === 0 ? 0.35 : 1,
                                    }}>
                                    Up
                                </ButtonBase>
                                <ButtonBase
                                    disabled={index === draftFields.length - 1}
                                    onClick={() => moveField(field.id, 1)}
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        borderRadius: '7px',
                                        padding: '7px 9px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        opacity: index === draftFields.length - 1 ? 0.35 : 1,
                                    }}>
                                    Down
                                </ButtonBase>
                                <ButtonBase
                                    onClick={() =>
                                        setDraftFields((current) =>
                                            current.filter((item) => item.id !== field.id),
                                        )
                                    }
                                    sx={{
                                        border: `1px solid ${C.coral}33`,
                                        background: C.coralL,
                                        color: C.coral,
                                        borderRadius: '7px',
                                        padding: '7px 9px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}>
                                    Remove
                                </ButtonBase>
                            </Stack>
                        </Box>
                    ))}

                    {!draftFields.length && (
                        <Typography sx={{fontSize: 12, color: C.textMuted}}>
                            No fields selected.
                        </Typography>
                    )}

                    {error && (
                        <Typography sx={{fontSize: 12, color: C.coral, fontWeight: 700}}>
                            {error}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{padding: '10px 14px', gap: '6px'}}>
                <Button
                    onClick={() => setDraftFields(resetProductDetailFieldSettings().fields)}
                    sx={{fontWeight: 700}}>
                    Reset
                </Button>
                <Divider orientation="vertical" flexItem />
                <Button onClick={addField} sx={{fontWeight: 700}}>
                    Add field
                </Button>
                <Box sx={{flex: 1}} />
                <Button onClick={onClose} sx={{fontWeight: 700}}>
                    Cancel
                </Button>
                <Button
                    onClick={save}
                    disabled={saving}
                    variant="contained"
                    sx={{fontWeight: 800}}>
                    {saving ? 'Saving' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
